import { Component, Input, Output, EventEmitter, ElementRef, HostListener, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { ImportSource, ImportState, ImportScope, ImportScopeType, ImportStatus, ImportProgressDetail } from '../../models';
import { ProgressCircleComponent } from '../progress-circle/progress-circle.component';
import { ImportInfoCardComponent } from '../import-info-card/import-info-card.component';
import { ImportScopeComponent } from '../import-scope/import-scope.component';
import { TooltipModule } from 'primeng/tooltip';
import { Popover, PopoverModule } from 'primeng/popover';
import { InfoBannerComponent } from 'src/app/shared/components/info-banner/info-banner.component';
/**
 * Import Card Component
 *
 * Displays a complete import operation card including:
 * - Status badge and title
 * - Progress circle with clickable tooltip showing metric breakdown
 * - Metric detail popups (click info icon to see total/completed/error counts)
 * - Import info (time remaining, errors, dates)
 * - Scope configuration (full/customized)
 * - Start import button (disabled when pending/importing)
 */
@Component({
  selector: 'app-import-card',
  standalone: true,
  imports: [
    CommonModule,
    LocalizationPipe,
    ProgressCircleComponent,
    ImportInfoCardComponent,
    ImportScopeComponent,
    InfoBannerComponent,
    TooltipModule,
    PopoverModule,
  ],
  templateUrl: './import-card.component.html',
  styleUrls: ['./import-card.component.scss'],
})
export class ImportCardComponent {
  // Expose enums to template
  readonly ImportStatus = ImportStatus;
  readonly ImportSource = ImportSource;

  @Input() source: ImportSource = ImportSource.CA;
  @Input() title = '';
  @Input() iconClass = '';
  @Input() state: ImportState = this.getInitialState();
  @Input() systemStartDate: Date | null = null;
  @Input() isStarting: boolean = false;
  @Input() initialScopeType: ImportScopeType = 'full';

  @Output() startImport = new EventEmitter<ImportScope>();
  @Output() stopImport = new EventEmitter<void>();
  @Output() progressClick = new EventEmitter<void>();
  @Output() errorClick = new EventEmitter<void>();

  @ViewChild('metricDetailPopover') private metricDetailPopover?: Popover;

  scope: ImportScope = { type: 'full' };
  isProgressTooltipVisible = false;
  isMetricDetailVisible = false;
  selectedMetricId: number | null = null;
  isCollapsed = false;

  private readonly toasterService = inject(ToasterService);

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  get statusBadgeClass(): string {
    const statusName = ImportStatus[this.state.status];
    const statusClass = statusName
      .replace(/([A-Z])/g, '-$1')
      .toLowerCase()
      .replace(/^-/, '');
    return `status-badge ${statusClass}`;
  }

  get statusTextKey(): string {
    const statusName = ImportStatus[this.state.status];
    return `Settings::DataImport:Status:${statusName}`;
  }

  get canStartImport(): boolean {
    if (this.isStarting) {
      return false;
    }
    if (!this.state.systemStartDate) {
      return false;
    }
    if (this.state.status === ImportStatus.Pending || this.state.status === ImportStatus.Importing) {
      return false;
    }
    return true;
  }

  get canStopImport(): boolean {
    return this.state.status === ImportStatus.Pending || this.state.status === ImportStatus.Importing;
  }

  get buttonDisabledTooltip(): string {
    if (!this.state.systemStartDate) {
      return 'Settings::DataImport:Tooltip:SystemStartDateNotAvailable';
    }
    if (this.state.status === ImportStatus.Pending) {
      return 'Settings::DataImport:Tooltip:ImportPending';
    }
    if (this.state.status === ImportStatus.Importing) {
      return 'Settings::DataImport:Tooltip:ImportInProgress';
    }
    return '';
  }
  
  getProgressBarClass(detail: ImportProgressDetail): string {
    if (detail.errorRecords > 0) {
      return 'progress-bar-status-error';
    }
    const statusName = ImportStatus[this.state.status].toLowerCase();
    return `progress-bar-status-${statusName}`;
  }

  onStartImport(): void {
    if (!this.canStartImport) {
      return;
    }

    if (this.scope.type === 'customized' && this.scope.dateRange) {
      const { start, end } = this.scope.dateRange;
      if (!start || !end) {
        this.toasterService.info(
          'Settings::DataImport:Validation:DateRangeRequired',
          'Settings::DataImport:Validation:Title',
          undefined
        );
        return;
      }
      if (start >= end) {
        this.toasterService.warn(
          'Settings::DataImport:Validation:InvalidDateRange',
          'Settings::DataImport:Validation:Title',
          undefined
        );
        return;
      }
    }

    if (this.scope.type === 'customized') {
      if (!this.scope.selectedMetrics || this.scope.selectedMetrics.length === 0) {
        this.toasterService.warn(
          'Settings::DataImport:Validation:MetricsRequired',
          'Settings::DataImport:Validation:Title',
          undefined
        );
        return;
      }
    }

    this.startImport.emit(this.scope);
  }

  onStopImport(): void {
    if (!this.canStopImport) {
      return;
    }
    this.stopImport.emit();
  }

  onScopeChange(scope: ImportScope): void {
    this.scope = scope;
  }

  onProgressToggle(event?: MouseEvent): void {
    event?.stopPropagation();
    if (
      this.state.progressDetails &&
      this.state.progressDetails.length > 0
    ) {
      if (this.isProgressTooltipVisible) {
        this.closeProgressTooltip();
      } else {
        this.isProgressTooltipVisible = true;
      }
      this.progressClick.emit();
    }
  }

  onErrorClick(): void {
    this.errorClick.emit();
  }

  onTooltipClose(event: MouseEvent): void {
    event.stopPropagation();
    this.closeProgressTooltip();
  }

  onMetricInfoClick(detail: ImportProgressDetail, event: MouseEvent): void {
    event.stopPropagation();
    const popover = this.metricDetailPopover;
    // PrimeNG 21 no longer exposes isOverlayAnimationInProgress, so the "do not toggle mid-animation"
    // guard that used to sit here is gone. Re-check rapid clicks on the info icon if selectedMetricId
    // ever drifts from what the popover shows.
    if (!popover) {
      return;
    }
    this.selectedMetricId = detail.metricId;
    popover.toggle(event, event.currentTarget as HTMLElement);
  }

  onMetricDetailShow(): void {
    this.isMetricDetailVisible = true;
  }

  onMetricDetailHide(): void {
    this.isMetricDetailVisible = false;
  }

  trackByMetricId(_index: number, detail: ImportProgressDetail): number {
    return detail.metricId;
  }

  get selectedMetricDetail(): ImportProgressDetail | null {
    if (this.selectedMetricId === null) {
      return null;
    }
    return this.state.progressDetails?.find(d => d.metricId === this.selectedMetricId) || null;
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    if (this.isCollapsed) {
      this.closeProgressTooltip();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isProgressTooltipVisible) {
      return;
    }

    const target = event.target as HTMLElement;

    // The metric detail popover is appended to <body>, so it is outside this component's DOM.
    if (target.closest?.('.p-popover')) {
      return;
    }

    const progressTooltip = this.host.nativeElement.querySelector(
      '.import-progress-tooltip',
    ) as HTMLElement | null;

    if (progressTooltip?.contains(target)) {
      return;
    }

    this.closeProgressTooltip();
  }

  private closeProgressTooltip(): void {
    this.isProgressTooltipVisible = false;
    this.metricDetailPopover?.hide();
  }

  private getInitialState(): ImportState {
    return {
      status: ImportStatus.NotStarted,
      progress: 0,
      timeRemaining: null,
      errorCount: 0,
      totalRecords: 0,
      completedRecords: 0,
      pendingRecords: 0,
      systemStartDate: null,
      lastImport: null,
      progressDetails: [],
    };
  }
}

